/**
 * Freshness status categories for scouting cadence visualization.
 */
export type FreshnessStatus = 'green' | 'yellow' | 'red' | 'unknown';

/**
 * Cadence compliance status.
 */
export type CadenceCompliance = 'compliant' | 'overdue' | 'unknown';

/**
 * Configurable thresholds for scouting freshness status.
 * - greenDays: days since last scouting that still qualifies as "recently scouted"
 * - yellowDays: days since last scouting that qualifies as "needs attention soon"
 * Beyond yellowDays is "red" (requires attention).
 * Optional color fields allow tenant-specific hex colors for each level.
 */
export interface FreshnessThresholds {
  greenDays: number;
  yellowDays: number;
  greenColor?: string;
  yellowColor?: string;
  redColor?: string;
}

/** Default thresholds per spec: green <3 days, yellow 3–7 days, red >7 days */
export const DEFAULT_FRESHNESS_THRESHOLDS: FreshnessThresholds = {
  greenDays: 3,
  yellowDays: 7,
  greenColor: '#27ae60',
  yellowColor: '#f39c12',
  redColor: '#e74c3c',
};

/**
 * Calculates the number of whole days elapsed since the given ISO date string.
 * Returns null if the input is null or undefined.
 */
export function getDaysSinceDate(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null;
  const past = new Date(isoDate).getTime();
  const now = Date.now();
  return Math.floor((now - past) / 86400000);
}

/**
 * Returns the freshness status for an orchard based on days since last scouted.
 */
export function getFreshnessStatus(
  daysSince: number | null,
  thresholds: FreshnessThresholds
): FreshnessStatus {
  if (daysSince === null || daysSince === undefined) return 'unknown';
  if (daysSince <= thresholds.greenDays) return 'green';
  if (daysSince <= thresholds.yellowDays) return 'yellow';
  return 'red';
}

/** Maps freshness status to a display hex color, using optional tenant-configured colors. */
export function getFreshnessColor(status: FreshnessStatus, thresholds?: FreshnessThresholds): string {
  switch (status) {
    case 'green':   return thresholds?.greenColor  ?? '#27ae60';
    case 'yellow':  return thresholds?.yellowColor ?? '#f39c12';
    case 'red':     return thresholds?.redColor    ?? '#e74c3c';
    default:        return '#95a5a6';
  }
}

/**
 * Calculates the next scheduled scout date given the last scouted date
 * and a cadence interval in days.
 * Returns null if lastScoutedDate is null.
 */
export function getNextScheduledScout(
  lastScoutedDate: string | null | undefined,
  cadenceDays: number
): string | null {
  if (!lastScoutedDate) return null;
  const last = new Date(lastScoutedDate);
  const next = new Date(last.getTime() + cadenceDays * 86400000);
  return next.toISOString();
}

/**
 * Returns the cadence compliance status based on how many days have
 * passed since the last scout and the expected cadence interval.
 */
export function getCadenceCompliance(
  daysSince: number | null,
  cadenceDays: number
): CadenceCompliance {
  if (daysSince === null || daysSince === undefined) return 'unknown';
  return daysSince <= cadenceDays ? 'compliant' : 'overdue';
}
